import Button from '../../components/atoms/button';
import SVGIcon from '../../components/atoms/icons';
import AuthTemplate from '../../components/template/auth';
import Text from '../../components/atoms/text';
import Input from '../../components/atoms/input';
import { useCallback, useEffect, useState } from 'react';
import Label from '../../components/atoms/text/label';
import classNames from 'classnames';
import { NotificationToast } from '../../components/organisms/notification-toast';
import AuthService from '../../services/Auth.service';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ResetPassword = () => {
	const { token } = useParams();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();
	const isClient = searchParams.get('client') !== null ? true : false;

	const [error, setError] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const [form, setForm] = useState({
		password: '',
		confirmPassword: '',
		isMinLength: false,
		containsUppercase: false,
		containsLowercase: false,
		containsNumber: false,
		containsSpecialCharacter: false,
	});

	const validatePassword = (input: string) => ({
		isMinLength: input.length >= 8,
		containsUppercase: /[A-Z]/.test(input),
		containsLowercase: /[a-z]/.test(input),
		containsNumber: /\d/.test(input), // ou [0-9]
		containsSpecialCharacter: /[!@#$%^&*(),.?":{}|<>]/.test(input),
	});

	useEffect(() => {
		if (!error) return;
		NotificationToast.Warn(t(error));
		setError('');
	}, [error]);

	const handlePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
		const validations = validatePassword(e.target.value);
		setForm((prev) => ({
			...prev,
			...validations,
			password: e.target.value,
		}));
	};

	const handleConfirmPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
		setForm((prev) => ({
			...prev,
			confirmPassword: e.target.value,
		}));
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!form.password || !form.confirmPassword) {
			setError('alert.error.mandatory');
			return;
		}
		if (form.password !== form.confirmPassword) {
			setError('alert.error.password_compare');
			return;
		}
		if (
			!form.isMinLength ||
			!form.containsLowercase ||
			!form.containsNumber ||
			!form.containsSpecialCharacter ||
			!form.containsUppercase
		) {
			setError('alert.error.password_validation');
			return;
		}

		setLoading(true);
		const response = await AuthService.resetPassword(
			token || '',
			form.password,
			setError,
			isClient
		);
		if (response) {
			NotificationToast.Success(t('alert.success.password'));
			if (isClient) navigate('/client/login');
			else navigate('/login');
		}
		setLoading(false);
	};

	const renderValidation = (value: boolean, label: string) => (
		<Label className="text-white flex items-center gap-1">
			<div className="relative size-3 border border-gray-border rounded-full">
				<SVGIcon
					className={classNames(
						'absolute size-2 stroke-green left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
						{
							hidden: !value,
						}
					)}
					icon="Check"
				/>
			</div>
			{label}
		</Label>
	);

	return (
		<AuthTemplate title={t('login.reset_password')}>
			<form
				className="grid sm:grid-cols-[1fr_auto] gap-6 px-5 py-6 bg-[#ffffff3e] backdrop-blur-50 rounded-xl"
				onSubmit={handleSubmit}
			>
				<div className="flex flex-col gap-4 w-full md:w-96">
					<Text className="text-white" as="h2">
						{t('login.change_password')}
					</Text>
					<Input
						name="password"
						value={form.password}
						onChange={handlePassword}
						placeholder={t('misc.password')}
						type="password"
					/>
					<Input
						name="confirmPassword"
						value={form.confirmPassword}
						onChange={handleConfirmPassword}
						placeholder={t('misc.confirm_password')}
						type="password"
					/>
					<Button type="submit" loading={loading}>
						{t('login.change_password')}
					</Button>
				</div>
				<div className="flex flex-col gap-1 justify-center">
					<Text className="text-white">{t('login.security')}</Text>
					{renderValidation(form.isMinLength, t('login.min_length'))}
					{renderValidation(
						form.containsUppercase,
						t('login.contains_uppercase')
					)}
					{renderValidation(
						form.containsLowercase,
						t('login.contains_lowercase')
					)}
					{renderValidation(
						form.containsNumber,
						t('login.contains_number')
					)}
					{renderValidation(
						form.containsSpecialCharacter,
						t('login.contains_special')
					)}
				</div>
			</form>
		</AuthTemplate>
	);
};

export default ResetPassword;
